/**
 * Centralized text constants for the application.
 *
 * Organized by feature domain for easy maintenance.
 *
 * Usage:
 * - Import the needed constant object (e.g., AUTH_TEXT, DIALOG_TEXT).
 * - Reference strings directly: AUTH_TEXT.EMAIL_PLACEHOLDER.
 * - For dynamic strings, call the function: AUTH_TEXT.INCORRECT_CREDENTIALS_WITH_FORGOT(url).
 */

// Authentication & Registration
export const AUTH_TEXT = {
  // Page Titles
  SIGN_IN_TITLE: 'Sign In',
  SIGN_UP_TITLE: 'Sign Up',

  // Form Fields
  EMAIL_PLACEHOLDER: 'Email',
  PASSWORD_PLACEHOLDER: 'Password',
  LOGIN_BUTTON: 'Login',

  // Error Messages - Form Validation
  EMAIL_REQUIRED: 'Please enter a valid email address.',
  PASSWORD_REQUIRED: 'Please enter a valid password.',
  INCORRECT_CREDENTIALS: 'Incorrect email or password.',
  INCORRECT_CREDENTIALS_NEWLINE: 'Incorrect email or password. \nPlease try again.',
  INCORRECT_CREDENTIALS_WITH_FORGOT: (url: string) =>
    `Incorrect email or password. \nIf you have forgotten your password, visit\n${url}`,
  GENERIC_SIGNUP_ERROR: 'We are unable to register your account.\nPlease verify your credentials, and try again.',

  // Registration QR Flow - Sign Up
  QR_SIGN_UP_INSTRUCTION: (url: string) =>
    `Scan with any camera or QR code reader on your phone or tablet and complete the sign up process. Or go to ${url}`,

  // Registration QR Flow - Sign In
  QR_SIGN_IN_INSTRUCTION_PREFIX: 'Scan with any camera or QR code reader on your phone or tablet, or visit',
  QR_SIGN_IN_INSTRUCTION: (url: string) =>
    `Scan with any camera or QR code reader on your phone or tablet, or visit ${url}`,
  QR_ENTER_CODE: 'Enter this code:',

  // Buttons
  CONTINUE_TO_SIGN_IN: 'Continue to Sign In',
  USE_KEYBOARD_INSTEAD: 'Use Keyboard Instead',

  // Footer Information
  FORGOT_PASSWORD_INFO: (url: string) =>
    `If you don't have an account yet, or have forgotten your password, please visit ${url}`,

  // Loading States
  LOADING: 'Loading...',
  LOADING_LOWERCASE: 'loading...',
} as const;

// Dialogs & Errors
export const DIALOG_TEXT = {
  // Network Error Dialog
  NETWORK_ERROR_TITLE: 'Network Error',
  NETWORK_CONNECTION_LOST: 'Network Connection Lost',
  NETWORK_ERROR_MESSAGE: 'An error occurred while fetching the data from network. Try again later.',
  NETWORK_CHECK_CONNECTION: 'Please check your internet connection, and try again.',

  // Generic Error Dialog
  ERROR_TITLE: 'Error',
  ERROR_POPULATING_GRID: 'Error while populating the grid',

  // Exit Dialog
  EXIT_APP_TITLE: 'Exit App',
  EXIT_APP_MESSAGE: 'Are you sure you want to exit ?',

  // Sign Out Dialog
  SIGN_OUT_CONFIRMATION: 'Are you sure you want to sign out?',

  // More Options Dialog
  MORE_OPTIONS: 'More Options',

  // Common Buttons
  OK_BUTTON: 'Ok',
  CANCEL_BUTTON: 'Cancel',
  REFRESH_BUTTON: 'Refresh',
} as const;

// Tab Options
export const TAB_TEXT = {
  QR_CODE: 'QR Code',
  KEYBOARD: 'Keyboard',
} as const;

// General UI
export const UI_TEXT = {
  NO_RESULTS_FOUND: 'No results found',
} as const;

// Error Messages
export const ERROR_TEXT = {
  // Playback Errors
  PLAYBACK_ERROR_TITLE: 'Our servers missed their cue.',
  PLAYBACK_ERROR_MESSAGE: "Something's gone wrong behind the scenes. Please try again later.",
  PLAYBACK_ERROR_BUTTON: 'Exit Player',

  // Search Errors
  SEARCH_ERROR_TITLE: 'Our servers missed their cue.',
  SEARCH_ERROR_MESSAGE: "Something's gone wrong behind the scenes. Try again soon, or head back to the homepage.",
} as const;

// Settings Page
export const SETTINGS_TEXT = {
  // Settings Menu Options
  ACCOUNT_LABEL: 'Account',
  HELP_LABEL: 'Help & FAQs',
  TERMS_LABEL: 'Terms & Conditions',
  SIGN_OUT_LABEL: 'Sign Out',

  // Account Section
  EMAIL_LABEL: 'Email',
  ERROR_LOADING_ACCOUNT: 'Error loading account info',
  ERROR_RETRIEVING_ACCOUNT: 'Error retrieving account info',

  // Help Section
  APP_VERSION_LABEL: 'App Version',

  // Terms Section
  TERMS_TITLE: 'Terms & conditions',
} as const;

export const MY_LIST_TEXT = {
  NO_LIST_TITLE: 'Looks like you haven’t added anything yet',
  NO_LIST_DESCRIPTION: 'Explore content and tap the plus icon to keep track of what you would like to watch',
} as const;
